import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useLocation, useRoute } from 'wouter';
import { Gift } from 'lucide-react';

import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { ApiError, authedJson } from '@/lib/http';
import { POST_LOGIN_RETURN_KEY } from '@/lib/authUtils';
import { fetchJson, queryClient } from '@/lib/queryClient';

interface ClaimPayload {
  claim: {
    status: 'pending' | 'claimed' | 'canceled';
    punches: number;
    senderName: string;
    recipientName: string | null;
    message: string | null;
    passName: string;
    classes: Array<{ id: string; title: string }>;
    expiresAt: string | null;
    claimedByYou: boolean;
  };
}

function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      return (JSON.parse(err.body) as { error?: string }).error ?? err.message;
    } catch {
      return err.body || err.message;
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

export default function PassClaim() {
  const [, params] = useRoute('/passes/claim/:token');
  const token = params?.token || '';
  const [, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  const claimQuery = useQuery<ClaimPayload>({
    queryKey: ['/api/passes/claim', token, isAuthenticated],
    enabled: Boolean(token),
    queryFn: () => fetchJson<ClaimPayload>(`/api/passes/claim/${encodeURIComponent(token)}`),
  });
  const claim = claimQuery.data?.claim;

  const claimMutation = useMutation({
    mutationFn: () =>
      authedJson<{ pass: { classId: string; remainingSessions: number } }>(
        `/api/passes/claim/${encodeURIComponent(token)}`,
        { method: 'POST' }
      ),
    onSuccess: () => {
      toast({ title: 'Punches added to your account' });
      void queryClient.invalidateQueries({ queryKey: ['/api/passes/claim', token] });
      void queryClient.invalidateQueries({ queryKey: ['/api/classes/user/passes'] });
    },
    onError: err =>
      toast({
        title: 'Could not claim',
        description: apiErrorMessage(err),
        variant: 'destructive',
      }),
  });

  const signInToClaim = () => {
    try {
      window.sessionStorage.setItem(POST_LOGIN_RETURN_KEY, `/passes/claim/${token}`);
    } catch {
      // Storage unavailable — they'll land on the home page after sign-in.
    }
    navigate('/login');
  };

  const punchLabel = claim ? `${claim.punches} ${claim.punches === 1 ? 'punch' : 'punches'}` : '';

  return (
    <PublicShell routeKey="classes">
      <div className="page-shell-narrow py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Gift className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>{claim ? `${punchLabel} for you` : 'Punches to claim'}</CardTitle>
                <CardDescription>
                  {claim ? `${claim.senderName || 'Someone'} sent you ${punchLabel}.` : ' '}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {claimQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : claimQuery.isError || !claim ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                This link isn&apos;t valid. Ask the sender to send the punches again.
              </p>
            ) : (
              <>
                {claim.message && (
                  <blockquote className="border-l-2 pl-3 italic text-muted-foreground">
                    “{claim.message}”
                  </blockquote>
                )}
                <div className="rounded-xl border p-4 text-sm">
                  <p className="font-medium">{claim.passName}</p>
                  {claim.classes.length > 0 && (
                    <p className="mt-1 text-muted-foreground">
                      Good for:{' '}
                      {claim.classes.map((c, i) => (
                        <span key={c.id}>
                          {i > 0 && ', '}
                          <Link href={`/classes/${c.id}`} className="text-primary hover:underline">
                            {c.title}
                          </Link>
                        </span>
                      ))}
                    </p>
                  )}
                  {claim.expiresAt && (
                    <p className="mt-1 text-muted-foreground">
                      Expires {new Date(claim.expiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {claim.status === 'pending' && !claimMutation.isSuccess ? (
                  isAuthenticated ? (
                    <Button
                      className="w-full"
                      onClick={() => claimMutation.mutate()}
                      disabled={claimMutation.isPending}
                      data-testid="button-claim-punches"
                    >
                      {claimMutation.isPending ? 'Claiming…' : `Claim ${punchLabel}`}
                    </Button>
                  ) : (
                    <Button className="w-full" onClick={signInToClaim}>
                      Sign in or create an account to claim
                    </Button>
                  )
                ) : claim.status === 'claimed' || claimMutation.isSuccess ? (
                  <div className="space-y-3">
                    <p className="text-sm">
                      {claim.claimedByYou || claimMutation.isSuccess
                        ? 'These punches are in your account. Use one from any class page above.'
                        : 'These punches have already been claimed.'}
                    </p>
                    {(claim.claimedByYou || claimMutation.isSuccess) && (
                      <Button asChild variant="outline" className="w-full">
                        <Link href="/account">View my passes</Link>
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    The sender canceled this transfer, so the punches went back to them.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicShell>
  );
}
